import { supabase } from '@/lib/supabase/client';
import {
  isLaunchInUpcomingWindow,
  isRelevantLaunch,
  launchMatchesOriginalDates,
  sortLaunchesByInstallationDate,
} from '@/lib/utils/location-launches';
import type { LocationLaunch, LocationLaunchInsert, LocationLaunchUpdate } from '@/types/location-launches';

async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

export const locationLaunchesService = {
  async getAll(): Promise<LocationLaunch[]> {
    try {
      const { data, error } = await supabase
        .from('location_launches')
        .select('*')
        .order('installation_date', { ascending: true });

      if (error) {
        console.error('Error fetching location launches:', error);
        return [];
      }

      return ((data as LocationLaunch[]) || []).sort(sortLaunchesByInstallationDate);
    } catch (error) {
      console.error('Error fetching location launches:', error);
      return [];
    }
  },

  async getById(id: string): Promise<LocationLaunch | null> {
    try {
      const { data, error } = await supabase.from('location_launches').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching location launch:', error);
        return null;
      }

      return (data as LocationLaunch) || null;
    } catch (error) {
      console.error('Error fetching location launch:', error);
      return null;
    }
  },

  async getByLocationId(locationId: string): Promise<LocationLaunch[]> {
    try {
      const { data, error } = await supabase
        .from('location_launches')
        .select('*')
        .eq('location_id', locationId)
        .order('installation_date', { ascending: true });

      if (error) {
        console.error('Error fetching location launches by location ID:', error);
        return [];
      }

      return ((data as LocationLaunch[]) || []).sort(sortLaunchesByInstallationDate);
    } catch (error) {
      console.error('Error fetching location launches by location ID:', error);
      return [];
    }
  },

  async getPrimaryByLocationId(locationId: string): Promise<LocationLaunch | null> {
    try {
      const { data, error } = await supabase
        .from('location_launches')
        .select('*')
        .eq('location_id', locationId)
        .eq('is_primary', true)
        .maybeSingle();

      if (error) {
        console.error('Error fetching primary location launch:', error);
        return null;
      }

      return (data as LocationLaunch) || null;
    } catch (error) {
      console.error('Error fetching primary location launch:', error);
      return null;
    }
  },

  async getUpcomingWindow(): Promise<LocationLaunch[]> {
    const launches = await this.getAll();
    return launches.filter((launch) => isLaunchInUpcomingWindow(launch));
  },

  async getRelevantByLocation(locationId: string): Promise<LocationLaunch[]> {
    const launches = await this.getByLocationId(locationId);
    return launches.filter((launch) => isRelevantLaunch(launch));
  },

  async create(data: LocationLaunchInsert): Promise<LocationLaunch> {
    try {
      const userId = await getCurrentUserId();
      const insertData = {
        location_id: data.location_id,
        status: data.status ?? 'installation_pending',
        installation_date: data.installation_date ?? null,
        launch_date: data.launch_date ?? null,
        installation_type: data.installation_type ?? null,
        is_primary: data.is_primary ?? false,
        excluded_from_upcoming_launches: data.excluded_from_upcoming_launches ?? false,
        notes: data.notes ?? null,
        created_by: userId,
        updated_by: userId,
      };

      const { data: newLaunch, error } = await supabase
        .from('location_launches')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Error creating location launch:', error);
        throw new Error(`Failed to create location launch: ${error.message}`);
      }

      return newLaunch as LocationLaunch;
    } catch (error) {
      console.error('Error creating location launch:', error);
      throw error;
    }
  },

  async ensurePrimaryForLocation(input: {
    locationId: string;
    status: LocationLaunch['status'];
    installationDate?: string | null;
    launchDate?: string | null;
    installationType?: LocationLaunch['installation_type'];
    excludedFromUpcoming?: boolean;
    notes?: string | null;
  }): Promise<LocationLaunch> {
    const existing = await this.getPrimaryByLocationId(input.locationId);
    if (existing) {
      return existing;
    }

    return this.create({
      location_id: input.locationId,
      status: input.status,
      installation_date: input.installationDate ?? null,
      launch_date: input.launchDate ?? null,
      installation_type: input.installationType ?? null,
      is_primary: true,
      excluded_from_upcoming_launches: input.excludedFromUpcoming ?? false,
      notes: input.notes ?? 'Primary launch',
    });
  },

  async update(id: string, data: LocationLaunchUpdate): Promise<LocationLaunch> {
    try {
      const userId = await getCurrentUserId();
      const updateData: Record<string, unknown> = {
        updated_by: userId,
      };

      if (data.status !== undefined) updateData.status = data.status;
      if (data.installation_date !== undefined) updateData.installation_date = data.installation_date;
      if (data.launch_date !== undefined) updateData.launch_date = data.launch_date;
      if (data.installation_type !== undefined) updateData.installation_type = data.installation_type;
      if (data.is_primary !== undefined) updateData.is_primary = data.is_primary;
      if (data.excluded_from_upcoming_launches !== undefined) {
        updateData.excluded_from_upcoming_launches = data.excluded_from_upcoming_launches;
      }
      if (data.notes !== undefined) updateData.notes = data.notes;

      const { data: updatedLaunch, error } = await supabase
        .from('location_launches')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating location launch:', error);
        throw new Error(`Failed to update location launch: ${error.message}`);
      }

      if (!updatedLaunch) {
        throw new Error('Location launch not found');
      }

      return updatedLaunch as LocationLaunch;
    } catch (error) {
      console.error('Error updating location launch:', error);
      throw error;
    }
  },

  async setPrimary(locationId: string, launchId: string): Promise<LocationLaunch> {
    try {
      const launches = await this.getByLocationId(locationId);
      const targetLaunch = launches.find((launch) => launch.id === launchId);
      if (!targetLaunch) {
        throw new Error('Location launch not found');
      }

      const currentPrimary = launches.find((launch) => launch.is_primary) ?? null;
      if (currentPrimary?.id === launchId) {
        return targetLaunch;
      }

      const userId = await getCurrentUserId();

      if (currentPrimary) {
        const { error } = await supabase
          .from('location_launches')
          .update({ is_primary: false, updated_by: userId })
          .eq('id', currentPrimary.id);

        if (error) {
          console.error('Error demoting current primary location launch:', error);
          throw new Error(`Failed to update primary launch: ${error.message}`);
        }
      }

      return this.update(launchId, { is_primary: true });
    } catch (error) {
      console.error('Error setting primary location launch:', error);
      throw error;
    }
  },

  async restoreOriginalPrimaryForLocation(input: {
    locationId: string;
    status: LocationLaunch['status'];
    installationDate: string | null;
    launchDate: string | null;
    installationType?: LocationLaunch['installation_type'];
    excludedFromUpcoming?: boolean;
    notes?: string | null;
  }): Promise<LocationLaunch> {
    if (!input.installationDate && !input.launchDate) {
      throw new Error('The location does not have original installation or launch dates to restore.');
    }

    const launches = await this.getByLocationId(input.locationId);
    const matchingLaunch =
      launches.find((launch) =>
        launchMatchesOriginalDates(launch, input.installationDate, input.launchDate)
      ) ?? null;

    if (matchingLaunch) {
      const restoredPrimary = await this.setPrimary(input.locationId, matchingLaunch.id);
      return this.update(restoredPrimary.id, {
        status: input.status,
        installation_date: input.installationDate,
        launch_date: input.launchDate,
        installation_type: input.installationType ?? null,
        excluded_from_upcoming_launches: input.excludedFromUpcoming ?? false,
      });
    }

    const currentPrimary = launches.find((launch) => launch.is_primary) ?? null;
    if (currentPrimary) {
      await this.update(currentPrimary.id, { is_primary: false });
    }

    return this.create({
      location_id: input.locationId,
      status: input.status,
      installation_date: input.installationDate,
      launch_date: input.launchDate,
      installation_type: input.installationType ?? null,
      is_primary: true,
      excluded_from_upcoming_launches: input.excludedFromUpcoming ?? false,
      notes: input.notes ?? 'Restored original primary launch from location record',
    });
  },

  async hideFromUpcoming(id: string): Promise<LocationLaunch> {
    return this.update(id, { excluded_from_upcoming_launches: true });
  },
};
